import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Link2, TrendingUp, Smartphone, BarChart3, Star, ShoppingCart,
  SearchCheck, Users, Check, ArrowRight, Sparkles, ShieldCheck, Zap
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/a9363d82-f4dd-4e78-aefa-7c991aac40e7/images/74b47d7a9b7c650cfa07ab2ff20b7907294e4c7fb235049b5ec56c2c7d8ad8b2.png";
const AI_IMG = "https://static.prod-images.emergentagent.com/jobs/a9363d82-f4dd-4e78-aefa-7c991aac40e7/images/5149619533ac401e31bad93712954d356c4397aca09132101d68fe401ee4f5f5.png";
const RECEPTION_IMG = "https://images.pexels.com/photos/32978233/pexels-photo-32978233.jpeg";
const AVATAR_1 = "https://images.unsplash.com/photo-1758518727888-ffa196002e59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlcnNvbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NjcwMDk2MHww&ixlib=rb-4.1.0&q=85";
const AVATAR_2 = "https://images.pexels.com/photos/7905813/pexels-photo-7905813.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AVATAR_3 = "https://images.pexels.com/photos/19746166/pexels-photo-19746166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const features = [
  { icon: Building2, tag: "PMS", title: "Property Management System", desc: "Front desk, check-in/out, room assignments, housekeeping, and guest folios in real-time." },
  { icon: Link2, tag: "Channel Manager", title: "Multi-Channel Manager", desc: "Sync rates and inventory across 150+ OTAs simultaneously with real-time two-way sync." },
  { icon: TrendingUp, tag: "Revenue Mgmt", title: "Dynamic Revenue Manager", desc: "AI-powered rate recommendations based on market demand, competitors, and historical patterns." },
  { icon: Smartphone, tag: "Booking Engine", title: "Direct Booking Engine", desc: "Beautiful, mobile-optimised booking page on your site. Zero commission. Integrated payments." },
  { icon: BarChart3, tag: "Analytics", title: "Sales Analytics & Insights", desc: "Drill into RevPAR, ADR, occupancy trends with dashboards built for hoteliers, not accountants." },
  { icon: Star, tag: "Reviews", title: "Reputation Management", desc: "Monitor and respond to reviews across Google, TripAdvisor, OTAs from one unified inbox." },
  { icon: ShoppingCart, tag: "POS", title: "Cloud Point of Sale", desc: "Restaurant, spa, ancillary billing tied to guest folios. Real-time inventory and reporting." },
  { icon: SearchCheck, tag: "Rate Intel", title: "Rate Shopper", desc: "Live competitor pricing from OTAs, refreshed every few hours. Know your position always." },
  { icon: Users, tag: "B2B Agents", title: "Agent Connect", desc: "Give offline travel agents branded booking portals with custom rates and instant confirmation." },
];

const channels = [
  "Booking.com","MakeMyTrip","Airbnb","Goibibo","Agoda","Razorpay","PayU","Google Hotels",
  "TripAdvisor","Expedia","Hotels.com","Stripe","Yatra","Cleartrip","Trivago","Hostelworld",
];

const pricing = [
  { name: "Starter", price: "₹2,499", sub: "per month · up to 20 rooms", features: ["Property Management System","Up to 10 channel connections","Direct booking engine","Basic revenue reports","Email support (48hr SLA)"], cta: "Get Started", highlight: false },
  { name: "Professional", price: "₹5,999", sub: "per month · up to 80 rooms", features: ["Everything in Starter","Unlimited channel connections","AI Revenue Manager (Cerebrum)","Rate Shopper intelligence","Reputation management","Cloud POS (1 outlet)","Priority phone support"], cta: "Start Free Trial", highlight: true },
  { name: "Enterprise", price: "Custom", sub: "chains · groups · portfolios", features: ["Everything in Professional","Multi-property management","Central Reservation System","Custom API integrations","Dedicated account manager","99.9% SLA uptime guarantee"], cta: "Contact Sales", highlight: false },
];

const testimonials = [
  { q: "Our direct bookings went up 34% in the first quarter after switching to Booking4U. The channel manager is incredibly reliable — zero overbookings since day one.", name: "Vikram Rathore", role: "The Heritage Inn, Jaipur · 45 Rooms", img: AVATAR_2 },
  { q: "The AI revenue manager is like having a full-time revenue manager on staff. Our ADR improved by ₹800 per night with zero manual effort. Absolutely worth every rupee.", name: "Sneha Malhotra", role: "Shoreline Boutique, Goa · 62 Rooms", img: AVATAR_1 },
  { q: "We manage 6 properties across Karnataka with one login. The multi-property dashboard gives us a consolidated view that was impossible with our old tools. Game changer.", name: "Kiran Prasad", role: "Deccan Stays Group · 6 Properties", img: AVATAR_3 },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B101E] text-[#F8F9FA]" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-white/5" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-brand">
            <span className="w-8 h-8 rounded-md bg-[#D4AF37] flex items-center justify-center text-[#0B101E] font-bold font-display">B</span>
            <span className="font-display text-xl tracking-wide">Booking4U</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#features" className="hover:text-[#D4AF37] transition">Platform</a>
            <a href="#integrations" className="hover:text-[#D4AF37] transition">Integrations</a>
            <a href="#pricing" className="hover:text-[#D4AF37] transition">Pricing</a>
            <a href="#testimonials" className="hover:text-[#D4AF37] transition">Customers</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-white/80 hover:text-[#D4AF37] transition px-3 py-2" data-testid="nav-login">Sign in</Link>
            <Link to="/register" className="btn-gold rounded-md px-4 py-2 text-sm" data-testid="nav-signup">Start Free Trial</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B101E]/50 via-[#0B101E]/70 to-[#0B101E]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4AF37]/90 mb-6">
              <span className="w-8 h-px bg-[#D4AF37]/60" />
              Trusted by 8,400+ properties across India
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-light max-w-4xl">
              Hotel Revenue Management, <span className="italic gold-shine">Reimagined.</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">
              The all-in-one platform for independent hotels, boutique resorts, and chains. Manage bookings, revenue, channels, and operations — from one intelligent dashboard.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="btn-gold rounded-md px-6 py-3 inline-flex items-center gap-2" data-testid="hero-cta-trial">
                Start 30-Day Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#dashboard-preview" className="btn-ghost-gold rounded-md px-6 py-3 inline-flex items-center gap-2" data-testid="hero-cta-demo">
                See Live Demo <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>

          {/* Live card */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-20 glass rounded-2xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">Revenue</div>
              <div className="mt-2 font-display text-3xl">₹4.2L</div>
              <div className="text-xs text-emerald-400 mt-1">↑ 18% MoM</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">Occupancy</div>
              <div className="mt-2 font-display text-3xl">82%</div>
              <div className="text-xs text-emerald-400 mt-1">↑ 6 pts</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">ADR</div>
              <div className="mt-2 font-display text-3xl">₹3,840</div>
              <div className="text-xs text-white/50 mt-1">Stable</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50">RevPAR</div>
              <div className="mt-2 font-display text-3xl">₹3,213</div>
              <div className="text-xs text-emerald-400 mt-1">↑ 12%</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Platform Capabilities</div>
              <h2 className="font-display text-4xl md:text-5xl font-light max-w-xl leading-tight">Everything you need to run a hotel.</h2>
            </div>
            <p className="text-white/60 max-w-md">From reservation to revenue — Booking4U integrates every aspect of hotel management into one seamless platform.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="bg-[#0B101E] p-8 group hover:bg-[#12182B] transition-colors"
                data-testid={`feature-${f.tag.toLowerCase().replace(/\s/g,'-')}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-11 h-11 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37]/15 transition">
                    <f.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">{f.tag}</span>
                </div>
                <h3 className="font-display text-xl mb-3">{f.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD PREVIEW IMAGE BLOCK */}
      <section id="dashboard-preview" className="py-24 md:py-32 bg-[#0a0f1c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Cerebrum · AI Revenue</div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight mb-6">Your AI revenue manager, <em className="gold-shine not-italic">always on.</em></h2>
            <p className="text-white/65 leading-relaxed mb-8">
              Cerebrum analyses demand signals, competitor pricing, booking pace, and seasonality to recommend the optimal nightly rate — room by room, day by day. Accept with one click or review the reasoning.
            </p>
            <div className="space-y-4">
              {[
                { icon: Sparkles, t: "Claude Sonnet 4.5 powered", d: "Frontier reasoning for every rate decision." },
                { icon: ShieldCheck, t: "Zero overbookings", d: "Two-way sync with 150+ OTAs in under 30 seconds." },
                { icon: Zap, t: "Real-time channel updates", d: "Inventory and rate changes propagate instantly." },
              ].map((x, i) => (
                <div key={i} className="flex gap-4">
                  <div className="mt-1 w-8 h-8 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0 text-[#D4AF37]">
                    <x.icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-medium">{x.t}</div>
                    <div className="text-sm text-white/55">{x.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-[#D4AF37]/10 to-transparent blur-2xl rounded-3xl" />
            <img src={AI_IMG} alt="AI Revenue Manager visualization" className="relative rounded-2xl border border-white/10 w-full object-cover max-h-[70vh]" />
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section id="integrations" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">150+ Connected Channels</div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight">Works with your entire ecosystem.</h2>
            <p className="text-white/60 mt-5">Pre-built integrations with every major OTA, payment gateway, and hospitality tool — ready out of the box.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {channels.map((c, i) => (
              <div key={i} data-testid={`integration-${i}`} className="bg-[#12182B] border border-white/5 rounded-xl p-5 h-24 flex flex-col justify-center hover:border-[#D4AF37]/40 transition">
                <div className="font-display text-lg">{c}</div>
                <div className="text-xs text-white/40 mt-1">Connected</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 bg-[#0a0f1c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Simple Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-light">Grow without hidden costs.</h2>
            <p className="text-white/60 mt-5">No setup fees. No per-booking commissions. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-2xl p-8 border transition ${p.highlight ? "bg-[#12182B] border-[#D4AF37]/40 shadow-[0_0_60px_-20px_rgba(212,175,55,0.4)]" : "bg-[#12182B] border-white/10"}`}
                data-testid={`pricing-${p.name.toLowerCase()}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0B101E] text-[10px] tracking-widest uppercase px-3 py-1 rounded-full font-semibold">Most Popular</div>
                )}
                <div className="font-display text-2xl mb-2">{p.name}</div>
                <div className="font-display text-4xl">{p.price}</div>
                <div className="text-xs text-white/50 mt-1">{p.sub}</div>
                <ul className="mt-8 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/75">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className={`mt-8 block text-center rounded-md py-3 ${p.highlight ? "btn-gold" : "btn-ghost-gold"}`} data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Client Stories</div>
              <h2 className="font-display text-4xl md:text-5xl font-light max-w-xl leading-tight">Hoteliers love Booking4U.</h2>
            </div>
            <p className="text-white/60 max-w-md">From boutique homestays to 200-room business hotels — hear from our customers across India.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-[#12182B] border border-white/10 rounded-2xl p-8 hover:border-[#D4AF37]/30 transition" data-testid={`testimonial-${i}`}>
                <div className="text-[#D4AF37] mb-4">★★★★★</div>
                <p className="text-white/80 leading-relaxed">"{t.q}"</p>
                <div className="flex items-center gap-4 mt-6">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-white/10" />
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-white/50">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={RECEPTION_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B101E]/80" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Ready to get started?</div>
          <h2 className="font-display text-4xl md:text-6xl font-light leading-tight">
            Transform your hotel's <em className="gold-shine not-italic">revenue today.</em>
          </h2>
          <p className="text-white/65 mt-6 max-w-2xl mx-auto">Join 8,400+ properties already growing with Booking4U. Your first 30 days are completely free — no credit card needed.</p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/register" className="btn-gold rounded-md px-7 py-3.5 inline-flex items-center justify-center gap-2" data-testid="final-cta-trial">
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="btn-ghost-gold rounded-md px-7 py-3.5 inline-flex items-center justify-center gap-2" data-testid="final-cta-login">
              Sign in to your account
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10 text-sm text-white/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row justify-between gap-4">
          <div>© 2026 Booking4U — Hospitality Revenue Platform</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#D4AF37]">Privacy</a>
            <a href="#" className="hover:text-[#D4AF37]">Terms</a>
            <a href="#" className="hover:text-[#D4AF37]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
